import { useEffect, useRef, useState } from 'preact/hooks';
import { apiFetch } from '../api/client';

const endpoint = '/v1/owner/direct-booking';
const button = { minHeight: '44px', padding: '0.6rem 1rem', borderRadius: '8px', border: '1px solid var(--border-subtle)', background: 'white', font: 'inherit', cursor: 'pointer' };

function checkedStatus(data) {
  if (!data || !Number.isInteger(data.revision) || data.revision < 0 ||
      typeof data.enabled !== 'boolean' || typeof data.available !== 'boolean' ||
      (data.checked_at !== null && (!Number.isInteger(data.checked_at) || data.checked_at < 0))) {
    throw new Error('Direct booking status could not be verified. Reload to try again.');
  }
  if (data.url != null) {
    let url;
    try { url = new URL(data.url); } catch (_) { throw new Error('Square did not return a valid booking link.'); }
    if (url.protocol !== 'https:' || url.username || url.password || url.port || url.hash ||
        !['square.site', 'squareup.com'].some(host => url.hostname === host || url.hostname.endsWith(`.${host}`)) ||
        !(url.pathname === '/book' || url.pathname.startsWith('/book/') || url.pathname.startsWith('/appointments/'))) {
      throw new Error('Square did not return a valid booking link.');
    }
  }
  if (data.available && (!data.enabled || !data.url)) throw new Error('Reload direct booking status.');
  return data;
}

export function DirectBookingPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [needsReload, setNeedsReload] = useState(false);
  const alive = useRef(false);
  const inFlight = useRef(false);

  useEffect(() => { alive.current = true; load(); return () => { alive.current = false; }; }, []);

  async function load(method = 'GET') {
    if (inFlight.current || (method !== 'GET' && (!status || needsReload))) return;
    inFlight.current = true; setBusy(true); setError('');
    try {
      if (method !== 'GET') await apiFetch(endpoint, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expected_revision: status.revision }),
      });
      const next = checkedStatus(await apiFetch(endpoint));
      if (alive.current) { setStatus(next); setNeedsReload(false); }
    } catch (err) {
      if (alive.current) { setError(err.message || 'Direct booking could not be confirmed. Reload its status.'); setNeedsReload(true); }
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy(false);
    }
  }

  return <section aria-label="Direct Square booking" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-subtle)' }}>
    <h3 style={{ fontSize: '1rem', margin: 0 }}>Direct Square booking</h3>
    <p>If chat is unavailable, clients can book through your Square booking page. Square handles availability, payment requirements and confirmation.</p>
    {status?.available && <p><a href={status.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--sage)', textDecoration: 'underline' }}>Open your Square booking page</a></p>}
    {status && !status.available && <p role="status">{status.enabled ? 'The Square booking link is currently unavailable. Refresh to check it again.' : 'Direct Square booking is disabled. You can enable it below.'}</p>}
    {status?.checked_at != null && <p style={{ fontSize: '0.8125rem' }}>Last checked: {new Date(status.checked_at * 1000).toLocaleString()}</p>}
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
      <button type="button" style={button} disabled={busy || !status || needsReload} onClick={() => load('POST')}>{status?.enabled ? 'Refresh Square booking link' : 'Enable direct Square booking'}</button>
      {status?.enabled && <button type="button" style={button} disabled={busy || needsReload} onClick={() => load('DELETE')}>Disable direct Square booking</button>}
      <button type="button" style={button} disabled={busy} onClick={() => load()}>Reload direct booking status</button>
    </div>
    {busy && <p role="status">Checking direct booking…</p>}
    {error && <p role="alert" style={{ color: 'var(--coral)' }}>{error}</p>}
  </section>;
}
